import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  ferryApi,
  type AmendFerryBookingRequest,
  type CreateFerryBookingRequest,
  type FerryBooking,
  type FerryBookingEvent,
  type FerryBookingListFilter,
} from '../client';
import { ferryKeys } from './useKeys';

/**
 * Bookings of the organisation, filtered. A consumer lists its own with
 * `sourceApp` and a `from`/`to` window on the sailing, and joins them to its
 * rows by `externalReference`.
 */
export function useFerryBookings(
  organisationId: string | null,
  filter: FerryBookingListFilter = {},
  options?: Omit<UseQueryOptions<FerryBooking[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<FerryBooking[], Error>({
    queryKey: organisationId ? ferryKeys.bookings(organisationId, filter) : ferryKeys.bookingsAll,
    queryFn: () => ferryApi.getBookings(organisationId!, filter),
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useFerryBooking(
  id: string | null,
  options?: Omit<UseQueryOptions<FerryBooking, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<FerryBooking, Error>({
    queryKey: id ? ferryKeys.booking(id) : ferryKeys.bookingsAll,
    queryFn: () => ferryApi.getBooking(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** The booking's calls to the carrier, refused ones included. */
export function useFerryBookingEvents(id: string | null) {
  return useQuery<FerryBookingEvent[], Error>({
    queryKey: id ? ferryKeys.bookingEvents(id) : ferryKeys.bookingsAll,
    queryFn: () => ferryApi.getBookingEvents(id!),
    enabled: !!id,
  });
}

/** What every booking change invalidates: its lists, and the capacity it consumed or freed. */
function invalidateAfterChange(qc: ReturnType<typeof useQueryClient>, bookingId?: string) {
  if (bookingId) qc.invalidateQueries({ queryKey: ferryKeys.booking(bookingId) });
  qc.invalidateQueries({ queryKey: ferryKeys.bookingsAll });
  qc.invalidateQueries({ queryKey: ferryKeys.capacityAll });
}

/**
 * Create at the carrier. The caller mints `idempotencyKey` once per attempt
 * (see `ferryApi.createBooking`). Resolves with the booking even when the
 * carrier refused — check `integrationStatus` before treating it as booked.
 */
export function useCreateFerryBooking() {
  const qc = useQueryClient();
  return useMutation<
    FerryBooking,
    Error,
    { body: CreateFerryBookingRequest; idempotencyKey: string }
  >({
    mutationFn: ({ body, idempotencyKey }) => ferryApi.createBooking(body, idempotencyKey),
    onSuccess: (booking) => invalidateAfterChange(qc, booking.id),
  });
}

/** Amend at the carrier. Resolves with the booking even when the carrier refused — check `integrationStatus`. */
export function useAmendFerryBooking() {
  const qc = useQueryClient();
  return useMutation<FerryBooking, Error, { id: string; body: AmendFerryBookingRequest }>({
    mutationFn: ({ id, body }) => ferryApi.amendBooking(id, body),
    onSuccess: (_booking, { id }) => invalidateAfterChange(qc, id),
  });
}

export function useCancelFerryBooking() {
  const qc = useQueryClient();
  return useMutation<FerryBooking, Error, { id: string; keepCapacity: boolean }>({
    mutationFn: ({ id, keepCapacity }) => ferryApi.cancelBooking(id, keepCapacity),
    onSuccess: (_booking, { id }) => invalidateAfterChange(qc, id),
  });
}

export function useRefreshFerryBooking() {
  const qc = useQueryClient();
  return useMutation<FerryBooking, Error, string>({
    mutationFn: (id) => ferryApi.refreshBooking(id),
    onSuccess: (_booking, id) => invalidateAfterChange(qc, id),
  });
}
